import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Save,
  X,
  MapPin,
  Table as TableIcon,
  Users,
  AlertCircle,
  CheckSquare,
  Square,
  ArrowLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_tables_res,
  get_waiter_staff,
  save_update_dine_area,
  get_all_areas_res,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

export default function AddEditArea() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    areaName: "",
    areaType: "Family",
    containerCharge: "Default Configuration of order Types",
    serviceCharge: "Default Configuration of order Types",
  });

  // Tables and waiters
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedWaiters, setSelectedWaiters] = useState([]);
  const [initialApplied, setInitialApplied] = useState(false);

  const primary_id = location.state?.primary_id;

  // Area type options
  const areaTypeOptions = [
    { value: "Family", label: "Family" },
    { value: "Mini", label: "Mini" },
    { value: "Indoor", label: "Indoor" },
    { value: "Outdoor", label: "Outdoor" },
  ];

  // Charge options
  const chargeOptions = [
    {
      value: "Default Configuration of order Types",
      label: "Default Configuration",
    },
    { value: "Calculate Automatically", label: "Calculate Automatically" },
    {
      value: "Don't Calculate Automatically",
      label: "Don't Calculate Automatically",
    },
  ];

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    const fd = new FormData();
    fd.append("flag", 3);

    try {
      const response = await server_post_data(get_all_tables_res, fd);

      if (!response.data.error) {
        const tableData = response.data.message.data || [];
        setTables(tableData.map((t) => ({ ...t, checked: false })));
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  }, []);

  // Fetch waiters
  const fetchWaiters = useCallback(async () => {
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_waiter_staff, fd);

      if (!response.data.error) {
        const waiterData = response.data.message.data || [];
        setWaiters(waiterData.map((w) => ({ ...w, checked: false })));
      }
    } catch (error) {
      console.error("Error fetching waiters:", error);
    }
  }, []);

  // Fetch area data for edit
  const fetchAreaData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 2);
    fd.append("call_id", primary_id);

    try {
      const response = await server_post_data(get_all_areas_res, fd);

      if (!response.data.error && response.data.message.data.length > 0) {
        const areaData = response.data.message.data[0];

        setFormData({
          areaName: areaData.dining_area_name || "",
          areaType: areaData.dining_area_type || "Family",
          containerCharge:
            areaData.dining_area_container_charge ||
            "Default Configuration of order Types",
          serviceCharge:
            areaData.dining_area_service_charge ||
            "Default Configuration of order Types",
        });

        // Set selected waiters from comma-separated IDs
        if (areaData.waiters_ids) {
          const waiterIds = areaData.waiters_ids
            .split(",")
            .map((id) => Number(id.trim()));
          setSelectedWaiters(waiterIds);
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching area:", error);
      toast({
        title: "Error",
        description: "Failed to load area data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [primary_id, toast]);

  // Handle table selection
  const handleTableToggle = useCallback((tableId) => {
    setSelectedTables((prev) =>
      prev.includes(tableId)
        ? prev.filter((id) => id !== tableId)
        : [...prev, tableId]
    );
  }, []);

  // Handle select all tables
  const handleSelectAllTables = useCallback(() => {
    if (selectedTables.length === tables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(tables.map((t) => t.primary_id));
    }
  }, [tables, selectedTables]);

  // Handle waiter selection
  const handleWaiterToggle = useCallback((waiterId) => {
    setSelectedWaiters((prev) =>
      prev.includes(waiterId)
        ? prev.filter((id) => id !== waiterId)
        : [...prev, waiterId]
    );
  }, []);

  // Handle select all waiters
  const handleSelectAllWaiters = useCallback(() => {
    if (selectedWaiters.length === waiters.length) {
      setSelectedWaiters([]);
    } else {
      setSelectedWaiters(waiters.map((w) => w.primary_id));
    }
  }, [waiters, selectedWaiters]);

  // Apply selected waiters to checkbox state
  useEffect(() => {
    if (!initialApplied && selectedWaiters.length > 0 && waiters.length > 0) {
      setInitialApplied(true);
    }
  }, [selectedWaiters, waiters, initialApplied]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.areaName.trim()) {
      newErrors.areaName = "Area name is required";
    }

    if (selectedTables.length === 0) {
      newErrors.tables = "Please select at least one table";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, selectedTables]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();

    if (primary_id) {
      fd.append("main_id", primary_id);
    }

    fd.append("dining_area_name", formData.areaName);
    fd.append("dining_area_type", formData.areaType);
    fd.append("dining_area_container_charge", formData.containerCharge);
    fd.append("dining_area_service_charge", formData.serviceCharge);
    fd.append("table_primary_ids", selectedTables.join(","));
    fd.append("waiter_primary_ids", selectedWaiters.join(","));

    try {
      const response = await server_post_data(save_update_dine_area, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: primary_id
            ? "Area updated successfully"
            : "Area created successfully",
        });
        navigate("/area-management");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save area",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    formData,
    selectedTables,
    selectedWaiters,
    primary_id,
    validateForm,
    toast,
    navigate,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate("/area-management");
  }, [navigate]);

  // Initial load
  useEffect(() => {
    fetchTables();
    fetchWaiters();
  }, [fetchTables, fetchWaiters]);

  useEffect(() => {
    if (primary_id) {
      fetchAreaData();
    }
  }, [primary_id, fetchAreaData]);

  return (
    <MainLayout>
      <Header
        title={primary_id ? "Edit Area" : "Add New Area"}
        subtitle={
          primary_id
            ? "Update dining area information"
            : "Create a new dining area"
        }
      />

      <div className="p-6 space-y-6">
      <Button
            variant="ghost"
            onClick={handleCancel}
            className="gap-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Recipes
          </Button>
        {/* Basic Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Area Information
            </CardTitle>
            <CardDescription>
              Configure dining area details and settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Area Type */}
              <div className="space-y-2">
                <Label>
                  Area Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.areaType}
                  onValueChange={(value) =>
                    handleFieldChange("areaType", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {areaTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Area Name */}
              <div className="space-y-2">
                <Label>
                  Area Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g., VIP Section, Garden Area"
                  value={formData.areaName}
                  onChange={(e) =>
                    handleFieldChange("areaName", e.target.value)
                  }
                  className={errors.areaName ? "border-destructive" : ""}
                />
                {errors.areaName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.areaName}
                  </p>
                )}
              </div>

              {/* Container Charge */}
              <div className="space-y-2">
                <Label>Container Charge</Label>
                <Select
                  value={formData.containerCharge}
                  onValueChange={(value) =>
                    handleFieldChange("containerCharge", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {chargeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service Charge */}
              <div className="space-y-2">
                <Label>Service Charge</Label>
                <Select
                  value={formData.serviceCharge}
                  onValueChange={(value) =>
                    handleFieldChange("serviceCharge", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {chargeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tables Selection Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <TableIcon className="w-4 h-4 text-primary" />
                  Select Tables <span className="text-destructive">*</span>
                </CardTitle>
                <CardDescription>
                  Choose tables to assign to this area
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllTables}
                className="gap-2"
              >
                {selectedTables.length === tables.length ? (
                  <>
                    <CheckSquare className="w-4 h-4" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4" />
                    Select All
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {errors.tables && (
              <p className="text-sm text-destructive flex items-center gap-1 mb-4">
                <AlertCircle className="w-4 h-4" />
                {errors.tables}
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {tables.map((table) => (
                <div
                  key={table.primary_id}
                  className={cn(
                    "flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors",
                    selectedTables.includes(table.primary_id)
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50 hover:bg-muted"
                  )}
                  onClick={() => handleTableToggle(table.primary_id)}
                >
                  <Checkbox
                    checked={selectedTables.includes(table.primary_id)}
                    onCheckedChange={() => handleTableToggle(table.primary_id)}
                  />
                  <label className="text-sm font-medium cursor-pointer flex-1">
                    {table.table_code}
                  </label>
                </div>
              ))}
            </div>
            {tables.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No tables available. Please create tables first.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Waiters Selection Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Assign Waiters
                </CardTitle>
                <CardDescription>
                  Select waiters who will serve in this area
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllWaiters}
                className="gap-2"
              >
                {selectedWaiters.length === waiters.length ? (
                  <>
                    <CheckSquare className="w-4 h-4" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4" />
                    Select All
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {waiters.map((waiter) => (
                <div
                  key={waiter.primary_id}
                  className={cn(
                    "flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors",
                    selectedWaiters.includes(waiter.primary_id)
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50 hover:bg-muted"
                  )}
                  onClick={() => handleWaiterToggle(waiter.primary_id)}
                >
                  <Checkbox
                    checked={selectedWaiters.includes(waiter.primary_id)}
                    onCheckedChange={() =>
                      handleWaiterToggle(waiter.primary_id)
                    }
                  />
                  <label className="text-sm font-medium cursor-pointer flex-1">
                    {waiter.admin_name}
                  </label>
                </div>
              ))}
            </div>
            {waiters.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No waiters available. Please add staff members first.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={showLoaderAdmin}
            className="gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={showLoaderAdmin}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            {showLoaderAdmin ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4" />
                {primary_id ? "Update Area" : "Create Area"}
              </>
            )}
          </Button>
        </div>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
